"use client";

import { InputGroup, InputGroupAddon, InputGroupInput } from "@health/ui/components/input-group";
import { cn } from "@health/ui/utils";
import { AlertCircle } from "lucide-react";

type AuthInputProps = {
  type: "text" | "email" | "password";
  name: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoComplete: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  variant?: "light" | "dark";
  icon?: React.ReactNode;
};

export function AuthInput({
  type,
  name,
  value,
  onChange,
  placeholder,
  autoComplete,
  required = false,
  disabled = false,
  error,
  className,
  variant = "light",
  icon,
}: AuthInputProps) {
  const errorClasses = {
    light: "text-[var(--color-error-text)]",
    dark: "text-red-300",
  };

  const iconColorClasses = {
    light: "text-(--color-text-light)",
    dark: "text-white/50",
  };

  // For light variant, use InputGroup with bottom border style
  if (variant === "light") {
    return (
      <div className="flex flex-col">
        <InputGroup
          className={cn(
            "border-0 border-b border-border rounded-none shadow-none h-auto",
            "bg-transparent",
            error && "border-(--color-error-text)",
          )}
        >
          {icon && (
            <InputGroupAddon align="inline-start" className={cn("pl-0 text-accent opacity-50")}>
              {icon}
            </InputGroupAddon>
          )}
          <InputGroupInput
            type={type}
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoComplete={autoComplete}
            required={required}
            disabled={disabled}
            aria-invalid={!!error}
            className={cn(
              "text-[15px] leading-[20.1px] tracking-[-0.4px] py-1.5 h-auto pl-0.5",
              "placeholder:text-text-light placeholder:opacity-50",
              "disabled:opacity-50",
              className,
            )}
          />
        </InputGroup>
        {error && (
          <div
            className={cn("flex items-center gap-2 text-xs px-2 mt-1", errorClasses[variant])}
            role="alert"
          >
            <AlertCircle size={12} />
            {error}
          </div>
        )}
      </div>
    );
  }

  // Dark variant - keep original implementation
  return (
    <div className="flex flex-col">
      <div className="relative flex gap-2 items-center pb-3">
        {icon && <div className={cn("shrink-0", iconColorClasses[variant])}>{icon}</div>}
        <input
          type={type}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          className={cn(
            "flex-1 px-4 py-3.5 rounded-xl border border-white/20 bg-black/10",
            "text-white text-base placeholder:text-white/50",
            "focus:border-white/40 outline-none transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            icon && "pl-12",
            className,
          )}
        />
      </div>
      {error && (
        <div
          className={cn("flex items-center gap-2 text-xs px-2 mt-1", errorClasses[variant])}
          role="alert"
        >
          <AlertCircle size={12} />
          {error}
        </div>
      )}
    </div>
  );
}
